/**
 * ダイアログのフォーカスの出し入れを、全部のモーダルでまとめて面倒をみる。
 *
 * - 開いたら（`data-visible="true"`）中の最初のボタン等へフォーカスを移す
 * - 閉じたら、開く前にフォーカスがあった所へ戻す
 * - 開いているあいだは Tab でモーダルの外へ出ない
 *
 * モーダルはそれぞれ別のクラスが作っているので、属性の変化を見張る形にしている
 * （クラスごとに同じ処理を書くと、どれかで必ず漏れる）。
 *
 * `.modal` のほか、`data-focus-trap="true"` の付いた枠（盤面の開始画面・結果画面）も同じに扱う。
 * 開くときに自分でフォーカスを中へ移すモーダル（設定など）は、見張りが気づく前にフォーカスが
 * 中へ入っているので、その直前にフォーカスがあった所を覚えておいて戻す（Esc で閉じると
 * body に落ちていた）。戻る先が無いときは `data-return-focus` のセレクタへ戻す。
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function isTrapCandidate(el: Element): el is HTMLElement {
  return el instanceof HTMLElement && (el.classList.contains('modal') || el.dataset.focusTrap !== undefined);
}

function isOpenModal(el: Element): el is HTMLElement {
  if (!isTrapCandidate(el) || el.dataset.visible !== 'true') return false;
  return el.classList.contains('modal') || el.dataset.focusTrap === 'true';
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if (el.hidden || el.offsetParent === null) return false;
    // たたんだ <details> の中身は見えないのに offsetParent を持つことがある（「ほかの共有」）
    const closed = el.closest('details:not([open])');
    if (closed && !(el.tagName === 'SUMMARY' && el.parentElement === closed)) return false;
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  });
}

export function installModalFocus(doc: Document = document): void {
  const returnTo = new WeakMap<HTMLElement, HTMLElement>();
  const stack: HTMLElement[] = [];
  // ひとつ前にフォーカスがあった所（開くときに自分でフォーカスを移すモーダル向け）
  let current: HTMLElement | null = null;
  let previous: HTMLElement | null = null;
  doc.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target === current) return;
    previous = current;
    current = target;
  });

  const onOpen = (modal: HTMLElement) => {
    if (stack.includes(modal)) return;
    const active = doc.activeElement;
    if (active instanceof HTMLElement && active !== doc.body && !modal.contains(active)) {
      returnTo.set(modal, active);
    } else if (previous && previous.isConnected && !modal.contains(previous)) {
      returnTo.set(modal, previous);
    }
    stack.push(modal);
    window.requestAnimationFrame(() => {
      if (modal.contains(doc.activeElement)) return;
      const card = modal.querySelector<HTMLElement>('.modal__card') ?? modal;
      (focusables(card)[0] ?? card).focus();
    });
  };

  const onClose = (modal: HTMLElement) => {
    const i = stack.indexOf(modal);
    if (i < 0) return;
    stack.splice(i, 1);
    const fallbackSelector = modal.dataset.returnFocus;
    const back =
      returnTo.get(modal) ?? (fallbackSelector ? doc.querySelector<HTMLElement>(fallbackSelector) : null);
    returnTo.delete(modal);
    if (back && back.isConnected && (modal.contains(doc.activeElement) || doc.activeElement === doc.body)) {
      back.focus({ preventScroll: true });
    }
  };

  const observer = new MutationObserver((records) => {
    for (const r of records) {
      const target = r.target;
      if (!(target instanceof HTMLElement) || !isTrapCandidate(target)) continue;
      if (isOpenModal(target)) onOpen(target);
      else onClose(target);
    }
  });
  observer.observe(doc.body, { subtree: true, attributes: true, attributeFilter: ['data-visible', 'data-focus-trap'] });

  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const top = stack[stack.length - 1];
    if (!top || !isOpenModal(top)) return;
    const list = focusables(top);
    if (list.length === 0) return;
    const first = list[0];
    const last = list[list.length - 1];
    const active = doc.activeElement;
    if (event.shiftKey && (active === first || !top.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !top.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  });
}
